<script setup>
import { reactive, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
  inbox: {
    type: Object,
    default: () => null,
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const store = useStore();

const formState = {
  uiFlags: useMapGetter('captainInboxes/getUIFlags'),
  inboxes: useMapGetter('inboxes/getInboxes'),
  captainInboxes: useMapGetter('captainInboxes/getRecords'),
  conversationAttributes: useMapGetter(
    'customAttributeDefinitions/getConversationCustomAttributes'
  ),
};

const state = reactive({
  inboxId: props.inbox?.id || null,
  allStages: props.inbox?.all_stages !== false && (!props.inbox?.kanban_stages || props.inbox?.kanban_stages?.length === 0),
  kanbanStages: Array.isArray(props.inbox?.kanban_stages) ? [...props.inbox.kanban_stages] : [],
});

watch(
  () => props.inbox,
  newInbox => {
    if (newInbox) {
      state.inboxId = newInbox.id;
      state.allStages = newInbox.all_stages !== false && (!newInbox.kanban_stages || newInbox.kanban_stages.length === 0);
      state.kanbanStages = Array.isArray(newInbox.kanban_stages) ? [...newInbox.kanban_stages] : [];
    }
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('customAttributeDefinitions/get', 'conversation_attribute');
});

const availableKanbanStages = computed(() => {
  const list = [];
  const defs = formState.conversationAttributes.value || [];
  
  // Look for definitions with attribute_values (such as 'servidor', 'etapa', etc.)
  defs.forEach(def => {
    if (def.attribute_values && Array.isArray(def.attribute_values) && def.attribute_values.length > 0) {
      def.attribute_values.forEach(val => {
        const trimmed = String(val).trim();
        if (trimmed && !list.includes(trimmed)) {
          list.push(trimmed);
        }
      });
    }
  });

  // Fallback defaults if no custom attribute definitions loaded yet
  if (list.length === 0) {
    return [
      'Recepção e Qualificação',
      'Holerite',
      'Proposta',
      'Autorizado',
      'Aguardando Assinatura',
      'Analise Viabiliadade',
      'Pasta Fechada',
      'Distribuido',
      'PRAZOS',
      'Com Pedido',
      'Pronto para Cumprir',
    ];
  }

  return list;
});

const validationRules = {
  inboxId: { required },
};

const inboxList = computed(() => {
  if (props.isEdit && props.inbox) {
    return [
      {
        value: props.inbox.id,
        label: props.inbox.name,
      },
    ];
  }

  const currentAssistantInboxIds = formState.captainInboxes.value.map(inbox => inbox.id);

  return formState.inboxes.value
    .filter(inbox => !currentAssistantInboxIds.includes(inbox.id))
    .map(inbox => ({
      value: inbox.id,
      label: inbox.name,
    }));
});

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(
  () => formState.uiFlags.value.creatingItem || formState.uiFlags.value.updatingItem
);

const getErrorMessage = (field, errorKey) => {
  return v$.value[field].$error
    ? t(`CAPTAIN.INBOXES.FORM.${errorKey}.ERROR`)
    : '';
};

const formErrors = computed(() => ({
  inboxId: getErrorMessage('inboxId', 'INBOX'),
}));

const stageError = computed(() => {
  if (!state.allStages && (!state.kanbanStages || state.kanbanStages.length === 0)) {
    return 'Selecione pelo menos uma etapa do funil.';
  }
  return '';
});

const toggleStage = stage => {
  const index = state.kanbanStages.indexOf(stage);
  if (index > -1) {
    state.kanbanStages.splice(index, 1);
  } else {
    state.kanbanStages.push(stage);
  }
};

const selectAllStages = () => {
  state.kanbanStages = [...availableKanbanStages.value];
};

const clearAllStages = () => {
  state.kanbanStages = [];
};

const handleCancel = () => emit('cancel');

const prepareInboxPayload = () => ({
  inboxId: state.inboxId,
  assistantId: props.assistantId,
  allStages: state.allStages,
  kanbanStages: state.allStages ? [] : state.kanbanStages,
  isEdit: props.isEdit,
});

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    return;
  }
  if (!state.allStages && (!state.kanbanStages || state.kanbanStages.length === 0)) {
    return;
  }

  emit('submit', prepareInboxPayload());
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <!-- Seleção da Caixa de Entrada -->
    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.INBOXES.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        v-if="!isEdit"
        id="inbox"
        v-model="state.inboxId"
        :options="inboxList"
        :has-error="!!formErrors.inboxId"
        :placeholder="t('CAPTAIN.INBOXES.FORM.INBOX.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        :message="formErrors.inboxId"
      />
      <div
        v-else
        class="px-3 py-2 text-sm rounded-lg bg-n-alpha-2 text-n-slate-12 border border-n-strong"
      >
        <span class="font-medium">{{ inbox?.name }}</span>
      </div>
    </div>

    <!-- Seção de Segmentação por Etapas do Kanban -->
    <div class="p-3.5 border rounded-xl bg-n-alpha-1 border-n-weak flex flex-col gap-3">
      <div class="flex items-center justify-between">
        <div class="flex flex-col">
          <span class="text-sm font-medium text-n-slate-12">
            Atuar em todas as etapas do Funil / Kanban
          </span>
          <span class="text-xs text-n-slate-11">
            Se ativado, o assistente responderá em qualquer conversa desta caixa.
          </span>
        </div>
        <Switch v-model="state.allStages" />
      </div>

      <!-- Seletor de Etapas Específicas do Kanban -->
      <div
        v-if="!state.allStages"
        class="flex flex-col gap-2 pt-2.5 border-t border-n-weak"
      >
        <div class="flex items-center justify-between">
          <label class="text-xs font-semibold text-n-slate-12 uppercase tracking-wide">
            Etapas Permitidas do Funil:
          </label>
          <div class="flex gap-2">
            <button
              type="button"
              class="text-xs text-n-brand hover:underline font-medium"
              @click="selectAllStages"
            >
              Todas
            </button>
            <span class="text-xs text-n-slate-8">|</span>
            <button
              type="button"
              class="text-xs text-n-slate-11 hover:underline font-medium"
              @click="clearAllStages"
            >
              Limpar
            </button>
          </div>
        </div>

        <div class="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto p-1">
          <button
            v-for="stage in availableKanbanStages"
            :key="stage"
            type="button"
            class="px-2.5 py-1 text-xs font-medium rounded-lg transition-all duration-150 flex items-center gap-1.5 select-none"
            :class="
              state.kanbanStages.includes(stage)
                ? 'bg-n-brand text-white shadow-sm ring-1 ring-n-brand'
                : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 hover:text-n-slate-12 border border-n-weak'
            "
            @click="toggleStage(stage)"
          >
            <i
              class="size-3.5"
              :class="state.kanbanStages.includes(stage) ? 'i-lucide-check' : 'i-lucide-circle-dashed'"
            />
            {{ stage }}
          </button>
        </div>

        <span v-if="stageError" class="text-xs text-red-500 font-medium mt-1">
          {{ stageError }}
        </span>
      </div>
    </div>

    <!-- Botões de Ação -->
    <div class="flex items-center justify-between w-full gap-3 mt-1">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAPTAIN.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="isEdit ? 'Salvar Alterações' : t('CAPTAIN.FORM.CREATE')"
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>
